/** Emailed links: accepting an invitation (`/invite/:token`) and confirming an ownership transfer
 * (`/ownership/:token`). The invitation page works signed out: it says who invites whom, then signs in (an existing
 * account) or signs up (a new person) and comes back here to accept. */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowRight, Crown, LogOut, UserPlus } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { api, errorMessage, unwrap } from "../api/client";
import { ROLE_TONE, useOrg } from "../api/org";
import { useAuth } from "../auth/auth";
import { Alert, Badge, Button, Spinner } from "../components/ui";
import { date } from "../lib/format";
import { landingUrl } from "../config";
import { useT, useIntlShape } from "../i18n/I18nProvider";
import { DirIcon } from "../i18n/mirror";
import type { MessageKey } from "../i18n/keys";

const ROLE_LABELS: Record<string, MessageKey> = {
  owner: "shell.role.owner",
  admin: "shell.role.admin",
  designer: "shell.role.designer",
  sales: "shell.role.sales",
  viewer: "shell.role.viewer",
};

function Frame({ children }: { children: React.ReactNode }) {
  const t = useT();
  return (
    <div className="invite-page">
      <div className="card invite-card">
        <div className="card-body stack">
          <a href={landingUrl()} className="brand" style={{ padding: 0 }} title={t("shell.brand.website")}>
            <img src="/solaros-mark.png" alt="" />
            <div>
              <b style={{ color: "var(--text)" }}>SolarOS</b>
            </div>
          </a>
          {children}
        </div>
      </div>
    </div>
  );
}

export function AcceptInvitation() {
  const t = useT();
  const intl = useIntlShape();
  const { token = "" } = useParams();
  const auth = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const preview = useQuery({
    queryKey: ["invitation", token],
    queryFn: () => unwrap(api.GET("/v1/invitations/{token}", { params: { path: { token } } })),
    retry: false,
  });
  const accept = useMutation({
    mutationFn: () => unwrap(api.POST("/v1/invitations/{token}/accept", { params: { path: { token } } })),
    onSuccess: async (m) => {
      try {
        localStorage.setItem("solaros.org", m.organization_id);
        localStorage.removeItem("solaros.adminOrg");
      } catch {
        /* private mode */
      }
      await qc.invalidateQueries({ queryKey: ["me"] });
      window.location.assign("/designs"); // a fresh shell on the new company
    },
  });
  const returnTo = `/invite/${token}`;
  if (preview.isLoading || !auth.ready) return <Spinner label={t("auth.invite.opening")} />;
  if (preview.error || !preview.data)
    return (
      <Frame>
        <Alert tone="danger" title={t("auth.invite.broken")}>
          {errorMessage(preview.error)} {t("auth.invite.askResend")}
        </Alert>
      </Frame>
    );
  const inv = preview.data;
  const signedInAs = auth.email?.toLowerCase() ?? null;
  const wrongAccount = auth.signedIn && signedInAs && signedInAs !== inv.email;
  return (
    <Frame>
      <div>
        <h1 style={{ fontSize: 22 }}>{t("auth.invite.join", { org: inv.organization_name })}</h1>
        <p className="muted" style={{ marginTop: 6 }}>
          {intl.formatMessage(
            { id: inv.invited_by ? "auth.invite.invitedBy" : "auth.invite.invitedBySomeone" },
            { inviter: inv.invited_by ?? "", email: inv.email, org: inv.organization_name, b: (c) => <b>{c}</b>, mail: (c) => <b dir="auto">{c}</b> },
          )}
        </p>
      </div>
      <div className="keybox" style={{ fontFamily: "var(--font-body)", justifyContent: "space-between" }}>
        <span className="muted">{t("auth.invite.yourRole")}</span>
        <Badge plain tone={ROLE_TONE[inv.role]}>{ROLE_LABELS[inv.role] ? t(ROLE_LABELS[inv.role]!) : inv.role}</Badge>
      </div>
      {inv.status !== "pending" ? (
        <Alert tone="warn" title={t("auth.invite.notPending", { status: inv.status })}>
          {t("auth.invite.askNew")}
        </Alert>
      ) : !auth.signedIn ? (
        <div className="stack">
          {inv.account_exists ? (
            <>
              <p className="small muted">{t("auth.invite.hasAccount", { email: inv.email })}</p>
              <Button variant="primary" size="lg" block icon={<DirIcon><ArrowRight /></DirIcon>} onClick={() => (auth.mode === "oidc" ? auth.signIn({ returnTo, loginHint: inv.email }) : navigate("/login", { state: { returnTo } }))}>
                {t("auth.invite.signInToAccept")}
              </Button>
            </>
          ) : (
            <>
              <p className="small muted">{t("auth.invite.newAccount", { email: inv.email, org: inv.organization_name })}</p>
              <Button variant="primary" size="lg" block icon={<UserPlus />} onClick={() => (auth.mode === "oidc" ? auth.signIn({ signup: true, returnTo, loginHint: inv.email }) : navigate("/signup", { state: { returnTo } }))}>
                {t("auth.invite.createAndAccept")}
              </Button>
            </>
          )}
          <p className="small faint">{t("auth.invite.expires", { date: date(inv.expires_at) })}</p>
        </div>
      ) : wrongAccount ? (
        <div className="stack">
          <Alert tone="warn" title={t("auth.invite.signedInAs", { email: signedInAs })}>
            {t("auth.invite.wrongAccount", { email: inv.email })}
          </Alert>
          <Button icon={<DirIcon><LogOut /></DirIcon>} onClick={() => auth.signOut()}>
            {t("shell.signOut")}
          </Button>
        </div>
      ) : (
        <div className="stack">
          <Button variant="primary" size="lg" block icon={<DirIcon><ArrowRight /></DirIcon>} loading={accept.isPending} onClick={() => accept.mutate()}>
            {t("auth.invite.acceptOpen", { org: inv.organization_name })}
          </Button>
          {accept.error && <Alert tone="danger">{errorMessage(accept.error)}</Alert>}
        </div>
      )}
    </Frame>
  );
}

export function ConfirmOwnership() {
  const t = useT();
  const { token = "" } = useParams();
  const { me } = useOrg();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const confirm = useMutation({
    mutationFn: () => unwrap(api.POST("/v1/ownership-transfers/{token}/confirm", { params: { path: { token } } })),
    onSuccess: async () => {
      await qc.invalidateQueries();
    },
  });
  return (
    <Frame>
      <div>
        <h1 style={{ fontSize: 22 }}>
          <Crown size={20} style={{ verticalAlign: -3 }} /> {t("auth.ownership.title")}
        </h1>
        <p className="muted" style={{ marginTop: 6 }}>
          {t("auth.ownership.body", { email: me.user.email })}
        </p>
      </div>
      {confirm.data ? (
        <div className="stack">
          <Alert tone="ok" title={t("auth.ownership.doneTitle")}>
            {t("auth.ownership.done", { email: confirm.data.to_email })}
          </Alert>
          <Button onClick={() => navigate("/team")}>{t("auth.ownership.openTeam")}</Button>
        </div>
      ) : (
        <div className="stack">
          <Button variant="primary" size="lg" block loading={confirm.isPending} onClick={() => confirm.mutate()}>
            {t("auth.ownership.confirm")}
          </Button>
          {confirm.error && <Alert tone="danger">{errorMessage(confirm.error)}</Alert>}
        </div>
      )}
    </Frame>
  );
}
